import React, { useEffect, useRef, useState } from "react";
import { Upload as UploadIcon, FileCheck2, FileWarning, FileText } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import { api } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function UploadPage() {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [companies, setCompanies] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);

  const onSelect = (f) => {
    if (!f) return;
    setFile(f);
    setResult(null);
  };

  const onUpload = async () => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/records/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Inserted ${data.inserted} · skipped ${data.skipped}`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const csvTemplate = () => {
    const lines = [
      "companyId,sourceERP,recordType,amount,currency,date,category,department,description",
      ...companies.slice(0, 2).map(
        (c) =>
          `${c.company_id},${c.erp_system},SPEND,12345.67,USD,2026-01-15,IT,Engineering,Sample SPEND record for ${c.name}`
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "honey_badger_records_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Ingest Data"
        subtitle="Upload CSV / JSON records — bypasses live ERP connections"
        right={
          <button
            type="button"
            onClick={csvTemplate}
            data-testid="upload-template-button"
            className="flex h-9 items-center gap-1.5 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600 hover:text-navy-600"
          >
            <FileText className="h-3.5 w-3.5" />
            CSV template
          </button>
        }
      />

      <div className="p-6 animate-fade-in space-y-6" data-testid="upload-page">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                onSelect(e.dataTransfer.files?.[0]);
              }}
              onClick={() => inputRef.current?.click()}
              data-testid="upload-dropzone"
              className={[
                "flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed bg-white p-8 text-center transition-colors",
                dragOver
                  ? "border-brand-orange bg-brand-orange/5"
                  : "border-slate-200 hover:border-navy-600",
              ].join(" ")}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-navy-600 text-white">
                <UploadIcon className="h-5 w-5" />
              </div>
              <div className="font-display text-lg font-semibold text-navy-600">
                Drop a .csv or .json file here
              </div>
              <div className="text-xs text-slate-500">
                or click to choose from disk · Max 5 MB · 5,000 rows per file
              </div>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.json"
                hidden
                data-testid="upload-file-input"
                onChange={(e) => onSelect(e.target.files?.[0])}
              />
            </div>

            {file && (
              <div className="mt-4 flex items-center justify-between rounded-md border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-navy-600 text-white">
                    <FileText className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-navy-600">{file.name}</div>
                    <div className="text-xs text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onUpload}
                  disabled={busy}
                  data-testid="upload-submit-button"
                  className="h-9 rounded-sm bg-brand-orange px-4 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C] disabled:opacity-50"
                >
                  {busy ? "Uploading…" : "Ingest file"}
                </button>
              </div>
            )}

            {result && (
              <div
                className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
                data-testid="upload-result"
              >
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <FileCheck2 className="h-4 w-4" />
                    <span className="text-[10px] font-semibold uppercase tracking-widest">
                      Inserted
                    </span>
                  </div>
                  <div className="mt-1.5 font-display text-3xl font-semibold tabular text-emerald-700">
                    {result.inserted}
                  </div>
                </div>
                <div className="rounded-md border border-orange-200 bg-orange-50 p-4">
                  <div className="flex items-center gap-2 text-brand-orange">
                    <FileWarning className="h-4 w-4" />
                    <span className="text-[10px] font-semibold uppercase tracking-widest">
                      Skipped
                    </span>
                  </div>
                  <div className="mt-1.5 font-display text-3xl font-semibold tabular text-brand-orange">
                    {result.skipped}
                  </div>
                  {result.errors?.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-[11px] text-orange-800">
                      {result.errors.slice(0, 6).map((e, i) => (
                        <li key={i} className="truncate">• {e}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          <aside className="rounded-md border border-slate-200 bg-white p-5">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">
              File schema
            </div>
            <h3 className="mt-1 font-display text-base font-semibold text-navy-600">
              Required columns
            </h3>
            <ul className="mt-4 space-y-2 text-xs text-slate-600">
              <li><span className="mono font-semibold text-navy-600">companyId</span> — UUID, short_name, or company name</li>
              <li><span className="mono font-semibold text-navy-600">sourceERP</span> — e.g. Chempax, Syspro</li>
              <li><span className="mono font-semibold text-navy-600">recordType</span> — BUDGET / SPEND / REVENUE / FINE</li>
              <li><span className="mono font-semibold text-navy-600">amount</span> — decimal</li>
              <li><span className="mono font-semibold text-navy-600">date</span> — ISO-8601</li>
            </ul>
            <div className="mt-4 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              Optional
            </div>
            <ul className="mt-1 space-y-1 text-xs text-slate-500">
              <li>category, department, currency, description</li>
            </ul>
          </aside>
        </div>
      </div>
    </>
  );
}
